import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Clock } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AssessmentProductSuccess() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [purchase, setPurchase] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyPurchase = async () => {
      const sessionId = searchParams.get('session_id');
      const purchaseId = searchParams.get('purchase_id');

      if (!sessionId || !purchaseId) {
        setError('Missing payment verification parameters');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/assessment-product/verify/${purchaseId}?session_id=${sessionId}`
        );
        const data = await response.json();

        if (data.success) {
          setVerified(true);
          setPurchase(data.purchase);
        } else {
          setError(data.message || 'Payment verification failed');
        }
      } catch (err) {
        setError('An error occurred during verification');
      } finally {
        setLoading(false);
      }
    };

    verifyPurchase();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2F80ED] mx-auto mb-4"></div>
          <p className="text-[#64748B]">Verifying your purchase...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          {verified ? (
            <>
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-[#F0FDF4] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-[#15803D]" size={40} />
                </div>
                <h1 className="text-[36px] font-bold text-[#102A43] mb-4">
                  Thank you for your purchase!
                </h1>
                <p className="text-[18px] text-[#64748B]">
                  Your AI Accountability Assessment has been received.
                </p>
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Clock className="text-[#2F80ED]" size={24} />
                  <h2 className="text-[20px] font-bold text-[#102A43]">What happens next?</h2>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#2F80ED] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-[#102A43]">Confirmation email sent</p>
                      <p className="text-[14px] text-[#64748B]">Check your inbox for purchase confirmation</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#2F80ED] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-[#102A43]">Our team reviews your submission</p>
                      <p className="text-[14px] text-[#64748B]">We'll analyze your organization's AI governance posture</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#2F80ED] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-[#102A43]">Report delivered within 24 hours</p>
                      <p className="text-[14px] text-[#64748B]">Your complete assessment report will be sent to your email</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-[10px] p-4">
                  <p className="text-[14px] text-[#64748B]">
                    <strong>Purchase ID:</strong> {purchase?.id}
                  </p>
                  <p className="text-[14px] text-[#64748B]">
                    <strong>Organization:</strong> {purchase?.business_name}
                  </p>
                </div>
              </div>

              {/* Upsell to Declaration */}
              <div className="bg-[#102A43] rounded-[10px] p-8 text-center">
                <h2 className="text-[24px] font-bold text-white mb-3">
                  Ready to declare publicly?
                </h2>
                <p className="text-[16px] text-[#94A3B8] mb-6">
                  Take the next step with the AASC Accountability Declaration — $997/year
                </p>
                <Link
                  to="/apply"
                  className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
                >
                  Start Your Declaration
                  <ArrowRight size={18} />
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="w-20 h-20 bg-[#FEF2F2] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-[#B91C1C] text-4xl">!</span>
              </div>
              <h1 className="text-[32px] font-bold text-[#102A43] mb-4">
                Verification Issue
              </h1>
              <p className="text-[16px] text-[#64748B] mb-8">
                {error || 'We couldn\'t verify your payment. Please contact support.'}
              </p>
              <Link
                to="/assessment-product"
                className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
              >
                Try Again
              </Link>
            </div>
          )}
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default AssessmentProductSuccess;
